import { useState, useMemo } from "react";

export function usePagination(items, { initialPerPage = 10, searchFields = [] } = {}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialPerPage);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim() || searchFields.length === 0) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((item) =>
      searchFields.some((field) =>
        String(item[field] ?? "").toLowerCase().includes(query)
      )
    );
  }, [items, searchQuery, searchFields]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredItems.slice(start, start + itemsPerPage);
  }, [filteredItems, safePage, itemsPerPage]);

  const goToPage = (page) =>
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return {
    paginatedItems,
    filteredItems,
    currentPage: safePage,
    totalPages,
    itemsPerPage,
    searchQuery,
    totalItems: filteredItems.length,
    startIndex: filteredItems.length === 0 ? 0 : (safePage - 1) * itemsPerPage + 1,
    endIndex: Math.min(safePage * itemsPerPage, filteredItems.length),
    goToPage,
    setSearchQuery: handleSearchChange,
    setItemsPerPage: handleItemsPerPageChange,
  };
}
